/**
 * Sorrel House · <sh-pdp> (sections/sh-product.liquid).
 * - Galerie : vignettes desktop, carrousel mobile avec compteur « n / N » ; image de la variante.
 * - Options en boutons texte : variante resolue sur le JSON produit, prix, prix barre, URL ?variant=,
 *   valeurs indisponibles barrees, bouton desactive si epuise.
 * - Bloc du lot (coche par defaut) et ajouts « Add to the same box » : role=checkbox, clic, Espace, Entree.
 * - Bouton : lignes du panier dans data-lines (piece OU lot, puis ajouts coches), prix « · $X » resolu
 *   en direct sur les vrais prix ; l'ajout lui-meme passe par assets/sh-cart.js (data-sh-cart-add).
 * - Barre collante mobile une fois le bouton principal sorti de l'ecran, retiree des que la sentinelle de fin de fiche entre (§7.14).
 * - Videos clientes : lecture en grand dans un <dialog>, precedente / suivante.
 */
const readJSON = (el) => { try { return el ? JSON.parse(el.textContent || 'null') : null; } catch (e) { return null; } };

class ShPdp extends HTMLElement {
  connectedCallback() {
    this.product = readJSON(this.querySelector('[data-sh-product-json]'));
    this.set = readJSON(this.querySelector('[data-sh-set-json]'));
    this.atc = this.querySelector('[data-sh-atc]');
    this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    this.fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency });
    if (!this.product) return;
    const params = new URLSearchParams(window.location.search);
    const vid = Number(params.get('variant'));
    this.variant = this.product.variants.find((v) => v.id === vid)
      || this.product.variants.find((v) => v.available) || this.product.variants[0];
    this.bindGallery();
    this.bindOptions();
    this.bindChecks();
    this.bindBar();
    this.bindVideos();
    this.refresh(false);
  }

  money(cents) { return this.fmt.format((Number(cents) || 0) / 100); }

  /* ---------- Galerie ---------- */
  bindGallery() {
    this.slides = [...this.querySelectorAll('.sh-pdp__slide')];
    this.thumbs = [...this.querySelectorAll('[data-sh-thumb]')];
    this.counter = this.querySelector('[data-sh-counter]');
    const track = this.querySelector('[data-sh-slides]');
    if (!track || this.slides.length < 2) return;
    track.setAttribute('data-ready', '');
    this.show(0, false);
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(Number(t.dataset.shThumb), true)));
    track.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      this.markThumb(i);
    }, { passive: true });
  }
  show(i, scrollMobile) {
    if (!this.slides.length) return;
    const n = Math.max(0, Math.min(this.slides.length - 1, i));
    this.slides.forEach((s, k) => s.classList.toggle('is-on', k === n));
    this.markThumb(n);
    if (this.counter) this.counter.textContent = `${n + 1} / ${this.slides.length}`;
    const track = this.querySelector('[data-sh-slides]');
    if (scrollMobile && track && track.scrollWidth > track.clientWidth + 2) {
      track.scrollTo({ left: n * track.clientWidth, behavior: 'auto' });
    }
  }
  // Vignette active + rangée de vignettes recentrée sans faire défiler la page (mobile)
  markThumb(n) {
    this.thumbs.forEach((t, k) => t.classList.toggle('is-on', k === n));
    const row = this.querySelector('.sh-pdp__thumbs');
    const t = this.thumbs[n];
    if (row && t && row.scrollWidth > row.clientWidth + 2) {
      const left = row.scrollLeft + (t.getBoundingClientRect().left - row.getBoundingClientRect().left) - (row.clientWidth - t.clientWidth) / 2;
      row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }
  showMedia(mediaId) {
    if (!mediaId) return;
    const i = this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
    if (i >= 0) this.show(i, true);
  }

  /* ---------- Options ---------- */
  bindOptions() {
    this.fieldsets = [...this.querySelectorAll('[data-sh-option]')];
    this.fieldsets.forEach((fs) => {
      fs.querySelectorAll('.sh-pdp__optval').forEach((btn) => {
        btn.addEventListener('click', () => {
          fs.querySelectorAll('.sh-pdp__optval').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          const cur = fs.querySelector('[data-sh-optcur]');
          if (cur) cur.textContent = btn.dataset.value;
          this.pickFromOptions();
        });
      });
    });
    this.syncOptionButtons();
  }
  selected() {
    const opts = [...(this.variant ? this.variant.options : [])];
    this.fieldsets.forEach((fs) => {
      const idx = Number(fs.dataset.shOption);
      const on = fs.querySelector('.sh-pdp__optval[aria-pressed="true"]');
      if (on) opts[idx] = on.dataset.value;
    });
    return opts;
  }
  pickFromOptions() {
    const want = this.selected();
    const found = this.product.variants.find((v) => v.options.every((o, i) => o === want[i]));
    if (found) {
      this.variant = found;
      if (found.featured_media) this.showMedia(found.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(found.id));
      window.history.replaceState(window.history.state, '', url.toString());
    }
    this.syncOptionButtons();
    this.refresh(true);
  }
  syncOptionButtons() {
    const cur = this.variant ? this.variant.options : [];
    this.fieldsets.forEach((fs) => {
      const idx = Number(fs.dataset.shOption);
      fs.querySelectorAll('.sh-pdp__optval').forEach((b) => {
        const test = [...cur]; test[idx] = b.dataset.value;
        const v = this.product.variants.find((x) => x.options.every((o, i) => o === test[i]));
        b.classList.toggle('is-unavailable', !v || !v.available);
        b.setAttribute('aria-pressed', String(cur[idx] === b.dataset.value));
      });
    });
  }

  /* ---------- Lot et ajouts ---------- */
  bindChecks() {
    const toggle = (el) => {
      const on = el.getAttribute('aria-checked') !== 'true';
      el.setAttribute('aria-checked', String(on));
      el.classList.toggle('is-on', on);
      this.refresh(true);
    };
    this.querySelectorAll('[data-sh-set], [data-sh-addon]').forEach((el) => {
      el.addEventListener('click', () => toggle(el));
      el.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(el); }
      });
    });
  }
  setVariant() {
    if (!this.set || !this.set.variants) return null;
    const want = this.variant ? this.variant.options : [];
    const match = this.set.variants.find((sv) => sv.available && want.length && want.every((o) => sv.options.includes(o)));
    return match || this.set.variants.find((sv) => sv.available) || null;
  }

  /* ---------- Prix, lignes, bouton ---------- */
  refresh() {
    const v = this.variant;
    if (!v) return;
    // Dimensions de la variante choisie (28/09/2026)
    if (this.dims === undefined) {
      try { this.dims = JSON.parse(this.querySelector('[data-sh-dims-json]')?.textContent || '{}'); } catch (e) { this.dims = {}; }
    }
    const dRow = this.querySelector('[data-sh-dims-row]');
    const dLine = this.dims[String(v.id)];
    if (dRow && Object.keys(this.dims).some((k) => this.dims[k])) {
      if (dLine) { dRow.querySelector('[data-sh-dims]').textContent = dLine; dRow.hidden = false; } else { dRow.hidden = true; }
    }
    const setBox = this.querySelector('[data-sh-set]');
    const withSet = Boolean(setBox && setBox.getAttribute('aria-checked') === 'true' && this.setVariant());
    const lines = [];
    let total = 0;
    let name = this.product.title;
    if (withSet) {
      const sv = this.setVariant();
      lines.push({ id: sv.id, quantity: 1 });
      total += sv.price;
      name = this.set.title;
    } else {
      lines.push({ id: v.id, quantity: 1 });
      total += v.price;
    }
    this.querySelectorAll('[data-sh-addon][aria-checked="true"]').forEach((a) => {
      lines.push({ id: Number(a.dataset.variant), quantity: 1 });
      total += Number(a.dataset.price) || 0;
    });
    const price = this.querySelector('[data-sh-price]');
    if (price) price.textContent = this.money(v.price);
    const cmp = this.querySelector('[data-sh-compare]');
    if (cmp) { cmp.textContent = v.compare_at_price > v.price ? this.money(v.compare_at_price) : ''; }
    const available = withSet ? true : v.available;
    if (this.atc) {
      this.atc.dataset.lines = JSON.stringify(lines);
      this.atc.disabled = !available;
      this.atc.textContent = available ? `${this.atc.dataset.label} · ${this.money(total)}` : this.atc.dataset.soldout;
    }
    const barName = this.querySelector('[data-sh-bar-name]');
    const barPrice = this.querySelector('[data-sh-bar-price]');
    if (barName) barName.textContent = name;
    if (barPrice) barPrice.textContent = this.money(total);
  }

  /* ---------- Barre collante mobile ---------- */
  bindBar() {
    const bar = this.querySelector('[data-sh-bar]');
    const wrap = this.querySelector('[data-sh-atc-wrap]');
    if (!bar || !wrap || !('IntersectionObserver' in window)) return;
    // Deux observateurs (Blueprint §35) : bouton de page sorti par le haut ET pied de page pas encore visible.
    let past = false;
    let atEnd = false;
    const apply = () => {
      const on = past && !atEnd;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', String(!on));
      const btn = bar.querySelector('[data-sh-bar-add]');
      if (btn) btn.tabIndex = on ? 0 : -1;
    };
    const headerH = parseInt(getComputedStyle(document.body).getPropertyValue('--sh-header-h'), 10) || 0;
    new IntersectionObserver(([e]) => {
      // CRO 08/10/2026 : barre visible dès que le bouton de page n'est pas à l'écran (au-dessus OU en dessous),
      // donc dès l'arrivée sur mobile quand le bouton est sous la ligne de flottaison.
      past = !e.isIntersecting;
      apply();
    }, { rootMargin: `-${headerH}px 0px 0px 0px` }).observe(wrap);
    const foot = this.querySelector('[data-sh-bar-end]') || document.querySelector('.sh-foot');
    if (foot) {
      new IntersectionObserver(([e]) => {
        atEnd = e.isIntersecting || e.boundingClientRect.top < 0;
        apply();
      }, { threshold: 0 }).observe(foot);
    }
    bar.querySelector('[data-sh-bar-add]')?.addEventListener('click', () => this.atc?.click());
  }

  /* ---------- Videos ---------- */
  bindVideos() {
    const box = this.querySelector('[data-sh-vids]');
    if (!box) return;
    const cards = [...box.querySelectorAll('[data-sh-vid]')];
    const dialog = box.querySelector('dialog');
    const big = box.querySelector('.sh-vids__big');
    if (!dialog || !big) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((es) => es.forEach((e) => {
        const vid = e.target.querySelector('video');
        if (!vid) return;
        if (e.isIntersecting) vid.play().catch(() => {}); else vid.pause();
      }));
      cards.forEach((c) => io.observe(c));
    }
    let i = 0;
    const open = (n) => {
      i = (n + cards.length) % cards.length;
      big.src = cards[i].dataset.src || '';
      big.poster = cards[i].dataset.poster || '';
      if (!dialog.open) dialog.showModal();
      big.play().catch(() => { big.muted = true; big.play().catch(() => {}); });
    };
    cards.forEach((c, n) => c.addEventListener('click', () => open(n)));
    box.querySelectorAll('[data-sh-vid-step]').forEach((b) => b.addEventListener('click', () => open(i + Number(b.dataset.shVidStep))));
    box.querySelector('[data-sh-vid-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { big.pause(); big.removeAttribute('src'); big.load(); });
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  }
}
if (!customElements.get('sh-pdp')) customElements.define('sh-pdp', ShPdp);
